import React from "react";
import type { PrintedLineFlag } from "./quantityCorrection";

/**
 * Under a line a person corrected before stock-in (D44 VR-5): what the vendor
 * printed for it (#55), and, when the printed total did not add up as printed,
 * the difference to take up with the vendor (#58).
 *
 * Display only. The warning is the server's stored `printedLineFlags` entry and
 * never blocks Confirm & sync, so it is kept out of the grid's cell flags, which
 * feed the counts and the cells Confirm & sync records as checked.
 */
export default function CorrectedLineNote({
  lineNo,
  printed,
  flag,
  billedAtEnteredPrice = false,
}: {
  lineNo: number | string;
  /** "Vendor printed: 8 CASE, 3,694.16", from `printedFiguresOf`. */
  printed: string | null;
  flag: PrintedLineFlag | null;
  /**
   * D77: the reviewer said the supplier printed the OLD price
   * (`supplierPrintedCorrectedPrice: false`). The flag is judged at that price,
   * but the line is still billed at the price entered, so "the bill uses the
   * corrected figure" would be false.
   */
  billedAtEnteredPrice?: boolean;
}) {
  if (!printed && !flag) return null;
  return (
    <div className="mb-1 space-y-1">
      {printed ? <div className="text-xs text-slate-500">{printed}</div> : null}
      {flag ? (
        <div
          role="note"
          aria-label={`Vendor's printed total for line ${lineNo}`}
          className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
        >
          {flag.issue}{" "}
          <span className="text-amber-800">
            {billedAtEnteredPrice
              ? "The bill keeps the price entered, not the one printed; "
              : "The bill uses the corrected figure; "}
            take the difference up with the vendor. This never blocks
            Confirm &amp; sync.
          </span>
        </div>
      ) : null}
    </div>
  );
}
